import { pamtiPolaziste } from '../lib/polaziste'

/**
 * Novo izdanje aplikacije (CLAUDE.md §13, dopunjeno dotjerivanje-3 §8).
 *
 * Service worker preuzima odmah (`skipWaiting` + `clients.claim` u `public/sw.js`)
 * i pritom briše predmemoriju prethodnog izdanja. To je namjerno: nakon deploya
 * nitko ne smije ostati na radniku koji poslužuje chunkove kojih na disku više
 * nema. Cijena je da otvorena stranica u tom trenutku drži staru okosnicu i stare
 * hasheve — pa mora saznati da je zamijenjena.
 *
 * Saznaje kroz `controllerchange`, a ne kroz poruku radnika: događaj je jedini
 * pouzdan znak da je stranicu preuzeo DRUGI radnik od onoga koji ju je poslužio.
 * Uvjet je da je stranica pri učitavanju već imala kontrolora — prva instalacija
 * ga nema, pa njezino preuzimanje nije nadogradnja i ne smije ništa javiti (inače
 * bi svaki prvi posjet završio ponudom za osvježavanje).
 *
 * **ODABRANO: `skipWaiting` + `clients.claim` + JEDAN kontroliran reload**
 * (dotjerivanje-3 §8 nudi ovo ili samu traku; ovo je jednostavnije, jer traka je
 * ionako već tu i ne treba joj druga mehanika).
 *
 * Vidljiva kartica dobiva PITANJE, sakrivena se osvježava tiho: reload pod
 * prstima briše ispunjenu formu, a reload kartice u pozadini ne briše ništa — sve
 * što ekrani prikazuju ionako se čita `GET`-om (§16, 2026-07-31, gost PWA,
 * odluka 8).
 *
 * **A ono što traka nije držala: ISHOD.** Traka koju nitko ne tapne ostajala je
 * stajati, a stranica na starom buildu — dakle točno ona "mješavina stare i nove
 * ljuske" zbog koje ovaj krug postoji: dio chunkova iz predmemorije koja više ne
 * postoji, dio s diska na kojem tih imena više nema. Zato je reload sada
 * NEIZBJEŽAN, samo pristojan: čim kartica ode u pozadinu (druga aplikacija,
 * zaključan zaslon, prijelaz na drugi tab), osvježava se sama. Deploy tako
 * deterministički završi na novom buildu pri prvom sljedećem otvaranju, a da
 * nikome ništa nije nestalo ispod prsta.
 */

export interface SwOkolina {
  /** `navigator.serviceWorker`; `null` bez podrške (i u testu). */
  sw: ServiceWorkerContainer | null
  /** Je li kartica u pozadini — tada se osvježava bez pitanja. */
  sakriveno: () => boolean
  ponovoUcitaj: () => void
  /** Pretplata na povratak kartice u prvi plan; vraća odjavu. */
  naPovratak: (slusac: () => void) => () => void
  /**
   * Pretplata na ODLAZAK kartice u pozadinu; vraća odjavu.
   *
   * Postoji zbog jednog jedinog posla (dotjerivanje-3 §8): stranica koja je
   * ponudu za osvježavanje dobila, a korisnik ju je ostavio stajati, mora se
   * osvježiti sama čim prestane biti gledana. Trenutak odlaska je jedini u kojem
   * reload ne može ništa uzeti ispod prsta.
   */
  naOdlazak: (slusac: () => void) => () => void
}

/**
 * Razmak između provjera novog izdanja. Preglednik sam provjerava `sw.js` tek pri
 * navigaciji, a instalirana aplikacija vozača zna stajati otvorena cijelu smjenu —
 * bez ovoga bi novi build dočekala tek sutra.
 */
export const PROVJERA_MS = 30 * 60 * 1000

export function zadanaOkolina(): SwOkolina {
  return {
    sw: typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null,
    sakriveno: () => globalThis.document?.visibilityState === 'hidden',
    ponovoUcitaj: () => globalThis.location?.reload(),
    naPovratak(slusac) {
      const dokument = globalThis.document
      if (!dokument) return () => {}

      const naDogadaj = () => {
        if (dokument.visibilityState === 'visible') slusac()
      }

      dokument.addEventListener('visibilitychange', naDogadaj)
      return () => dokument.removeEventListener('visibilitychange', naDogadaj)
    },
    naOdlazak(slusac) {
      const dokument = globalThis.document
      if (!dokument) return () => {}

      const naDogadaj = () => {
        if (dokument.visibilityState === 'hidden') slusac()
      }

      dokument.addEventListener('visibilitychange', naDogadaj)
      return () => dokument.removeEventListener('visibilitychange', naDogadaj)
    },
  }
}

/**
 * Registrira radnika i javlja kad novo izdanje preuzme stranicu. Vraća odjavu —
 * slušači i mjerač ne smiju preživjeti odmontiranje (StrictMode montira dvaput).
 */
export function pratiIzdanje(
  javiNovo: () => void,
  okolina: SwOkolina = zadanaOkolina(),
  provjeraMs: number = PROVJERA_MS,
): () => void {
  const sw = okolina.sw
  if (!sw) return () => {}

  const imaoKontrolora = Boolean(sw.controller)
  let javljeno = false
  let odjaviOdlazak: (() => void) | undefined

  function naPromjenuKontrolora() {
    // Prvo preuzimanje nije nadogradnja, a jednom javljeno izdanje se ne javlja
    // dvaput — tiho osvježavanje inače postaje petlja.
    if (!imaoKontrolora || javljeno) return
    javljeno = true

    if (okolina.sakriveno()) {
      okolina.ponovoUcitaj()
      return
    }

    javiNovo()

    /*
      NEIZBJEŽAN, ALI PRISTOJAN RELOAD (dotjerivanje-3 §8).

      Traka je pitanje, ne dogovor: korisnik ju smije ignorirati, ali stranica
      pod njom radi na buildu kojeg na disku više nema. Zato se ovdje naoruža
      jedan jedini reload — onaj u trenutku kad kartica ode u pozadinu. Tada
      ispod prsta nema ničega (isto obrazloženje kao za tihi reload iznad), a
      povratak u aplikaciju je već novi build.
    */
    odjaviOdlazak = okolina.naOdlazak(() => {
      odjaviOdlazak?.()
      odjaviOdlazak = undefined
      okolina.ponovoUcitaj()
    })
  }

  sw.addEventListener('controllerchange', naPromjenuKontrolora)

  let mjerac: ReturnType<typeof setInterval> | undefined
  let odjaviPovratak: (() => void) | undefined

  void sw
    .register('/sw.js')
    .then((registracija) => {
      const provjeri = () => {
        // Pad provjere je gubitak svježine, ne kvar: sljedeći prolaz pokušava opet.
        void registracija.update().catch(() => {})
      }

      upisana = registracija
      mjerac = setInterval(provjeri, provjeraMs)
      odjaviPovratak = okolina.naPovratak(provjeri)
    })
    .catch(() => {
      /* Bez radnika aplikacija radi kao obična stranica — nema se što javiti. */
    })

  return () => {
    sw.removeEventListener('controllerchange', naPromjenuKontrolora)
    if (mjerac !== undefined) clearInterval(mjerac)
    odjaviPovratak?.()
    odjaviOdlazak?.()
    upisana = null
  }
}

/**
 * Registracija radnika, da se izdanje može provjeriti i IZVAN mjerača.
 *
 * Povlačenje za osvježavanje (krug 10, nalaz 4) je jedini trenutak u kojem
 * korisnik izrijekom traži "provjeri ponovno", pa je i jedini u kojem se isplati
 * pitati poslužitelj za novi build bez čekanja pola sata. Drži se ovdje, uz
 * pratnju koja ju je i dobila — dva mjesta koja registriraju radnika bila bi dva
 * radnika.
 */
let upisana: ServiceWorkerRegistration | null = null

/**
 * Provjeri ima li novo izdanje SADA. Nikad ne baca: pad provjere je gubitak
 * svježine, a ne razlog da povlačenje za osvježavanje izgleda kao greška.
 */
export async function provjeriIzdanje(): Promise<void> {
  try {
    await upisana?.update()
  } catch {
    /* Sljedeći prolaz mjerača pokušava opet. */
  }
}

/*
  Vanjski store: pratnju pokreće `main.tsx` pri pokretanju (kao i dosad), a traku
  prikazuje komponenta duboko u stablu — isti obrazac kao sesija (`auth/store.ts`).
*/

let novoIzdanje = false
const pretplatnici = new Set<() => void>()

export function pokreniPratnjuIzdanja(okolina?: SwOkolina): () => void {
  return pratiIzdanje(() => {
    novoIzdanje = true
    for (const slusac of pretplatnici) slusac()
  }, okolina)
}

export function pretplatiIzdanje(slusac: () => void): () => void {
  pretplatnici.add(slusac)
  return () => pretplatnici.delete(slusac)
}

export function imaNovoIzdanje(): boolean {
  return novoIzdanje
}

/** Samo za testove. */
export function _resetIzdanje(): void {
  novoIzdanje = false
  pretplatnici.clear()
  upisana = null
}

// "Objavljeno je novo izdanje" je procesno stanje i preživljava test (D15-A §1a).
pamtiPolaziste(_resetIzdanje)
